// PrecioBox — bloque de precio de la tienda v5 (kit-detalle y producto-detalle comparten estructura).
// Fuente: maquetas. Precio grande + unidad, precio comparado tachado, chip de ahorro,
// nota opcional (ej. "O $1.680 compra única") y slot `children` (ej. tags "Essential · 3–4 personas" o CTAs).
//
// Self-contained: <style>{PRECIO_CSS}</style> + clases .tv5-precio-* + tokens --tv5-* propios.
//
// Reglas de dato: precios redondeados hacia arriba (Math.ceil), sin centavos.
// El chip de ahorro muestra Math.max(12, ahorroPct) (mínimo comunicado 12%).
//
// Props:
//   precio         número            → precio principal (ej. 1490)
//   unidad         string            → sufijo (ej. '/mes suscripción', '· 2 litros · Rinde ~30 usos')
//   precioTachado  número|null       → precio comparado tachado (ej. 2043) → "$2.043 en súper"
//   ahorro         número|null       → monto de ahorro para el chip (ej. 553) → "💰 Ahorrás $553/mes"
//   ahorroPct      número|null       → % de ahorro → "X% menos que armarlo suelto" (usa Math.max(12,pct))
//   nota           ReactNode|null    → línea extra debajo (ej. compra única)
//   children       ReactNode         → slot arriba del precio (tags) o debajo (CTAs) según lo pases
import { Children } from 'react';

const fmt = (n) => '$' + Number(n).toLocaleString('es-UY');

const PRECIO_CSS = `
.tv5-precio {
  --tv5-teal:#00A8B5; --tv5-green:#10B981; --tv5-text:#F8F6F1;
  --tv5-muted:rgba(248,246,241,0.65); --tv5-dim:rgba(248,246,241,0.45);
  --tv5-border:rgba(255,255,255,0.08); --tv5-card:rgba(255,255,255,0.04);
  background:var(--tv5-card); border:1px solid var(--tv5-border); border-radius:20px; padding:22px; margin-top:6px;
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
}
.tv5-precio-slot { margin-bottom:14px; }
.tv5-precio-slot:empty { display:none; }
.tv5-precio-actual { display:flex; align-items:baseline; gap:10px; flex-wrap:wrap; padding-bottom:12px; border-bottom:1px dashed var(--tv5-border); }
.tv5-precio-num { font-size:42px; font-weight:900; letter-spacing:-0.02em; color:var(--tv5-text); line-height:1; }
.tv5-precio-unidad { font-size:14px; color:var(--tv5-muted); font-weight:600; }
.tv5-precio-tachado { font-size:14px; color:var(--tv5-dim); text-decoration:line-through; margin-left:auto; }
.tv5-precio-savings { display:flex; align-items:center; gap:8px; margin-top:10px; font-size:13px; color:var(--tv5-green); font-weight:700; flex-wrap:wrap; }
.tv5-precio-savings .chip { background:rgba(16,185,129,0.14); border:1px solid rgba(16,185,129,0.35); padding:4px 10px; border-radius:999px; }
.tv5-precio-nota { margin-top:8px; font-size:12px; color:var(--tv5-dim); }
.tv5-precio-nota b { color:var(--tv5-muted); font-weight:700; }
`;

export function PrecioBox({ precio, unidad, precioTachado = null, ahorro = null, ahorroPct = null, nota = null, children }) {
  const hasSlot = Children.count(children) > 0;
  const pctMostrado = ahorroPct != null ? Math.max(12, Math.round(ahorroPct)) : null;
  return (
    <div className="tv5-precio">
      <style>{PRECIO_CSS}</style>
      {hasSlot && <div className="tv5-precio-slot">{children}</div>}
      <div className="tv5-precio-actual">
        <span className="tv5-precio-num">{fmt(precio)}</span>
        {unidad && <span className="tv5-precio-unidad">{unidad}</span>}
        {precioTachado != null && <span className="tv5-precio-tachado">{fmt(precioTachado)} en súper</span>}
      </div>
      {(ahorro != null || pctMostrado != null) && (
        <div className="tv5-precio-savings">
          {ahorro != null && <span className="chip">💰 Ahorrás {fmt(ahorro)}</span>}
          {pctMostrado != null && <span>{pctMostrado}% menos que armarlo suelto en el súper</span>}
        </div>
      )}
      {nota && <div className="tv5-precio-nota">{nota}</div>}
    </div>
  );
}
